import React, { useCallback, useEffect, useState } from "react";
import BreadCrumbs from "../../Components/Other/breadcrumbs";
import "./catalogue.css";
import {
  viewExcel,
  assignCatalogue, // add this import from your actions
} from "../../Redux/Actions/franchiseAction";
import { ReactComponent as TowerIcon } from "../../Global Icons/CatalogueIcon/towers.svg";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import UnitDetailsModal from "../../Global Functions/catalogueModal";
import toast from "react-hot-toast";

const ViewCommercialCatalogue = () => {
  const { category_id, project_type, project_id } = useParams();
  const exceldata = useSelector((state) => state.franchises?.projectCatalogueExcel);
  const enabled = useSelector((state) => state.franchises?.enabled) || false;
  const enableError = useSelector((state) => state.franchises?.enableError) || null;

  // Added from franchises state for payload
  const {
    selectedBuilderId: builderId,
    selectedFranchiseId: franchiseId,
  } = useSelector((state) => state.franchises);

  const dispatch = useDispatch();

  const [towerID, setTowerID] = useState("");
  const [towerData, setTowerData] = useState();
  const [floors, setFloors] = useState([]);
  const [checked, setChecked] = useState(false);

  const [unitData, setUnitData] = useState();
  const [update, setUpdate] = useState(false);

  // State for selected units
  const [units, setUnits] = useState([]);

  // Helper to format floor labels
  const formatFloor = (floor) => (floor === 0 ? "G" : floor);

  // Helper to get min floor
  function getMinFloor(arr) {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    return Math.min(...arr);
  }

  // Helper to get max floor
  function getMaxFloor(arr) {
    if (!Array.isArray(arr) || arr.length === 0) return null;
    return Math.max(...arr);
  }

  // Transform catalogue data from floor-wise to column-wise units with floor range
  const transformToColumnWise = useCallback((floorDataArray) => {
    const unitMap = new Map();
    const unitSlots = Array.from({ length: 400 }, (_, i) => i + 1);

    for (const floorData of floorDataArray) {
      const rawFloor = floorData.floor;
      const currentFloor = rawFloor === 0 ? "G" : rawFloor;

      for (const [key, unit] of Object.entries(floorData)) {
        if (!/^\d+$/.test(key)) continue;
        if (typeof unit.unitno !== "string") continue;

        const unitIndex = parseInt(key);
        const position = unitSlots[unitIndex] || `Slot${unitIndex}`;

        const id = unit.unitno;
        const area = parseInt(unit["area(sq.ft)"]) || 0;
        const status = unit.status || "filtered";

        if (!unitMap.has(id)) {
          unitMap.set(id, {
            id,
            label: `${area} sq ft`,
            position,
            startFloor: currentFloor,
            endFloor: currentFloor,
            floor: currentFloor,
            status,
            floors: [currentFloor],
            tempData: unit,
          });
        } else {
          const existing = unitMap.get(id);
          existing.floors.push(currentFloor);
          existing.startFloor = getMinFloor(existing.floors);
          existing.endFloor = getMaxFloor(existing.floors);
        }
      }
    }
    return Array.from(unitMap.values()).map(({ floors, ...rest }) => rest);
  }, []);

  // Render catalogue for selected tower
  const renderCatalogue = useCallback(
    (item) => {
      if (!item || !exceldata?.data?.configuration) return;

      setTowerID(item._id);

      const matchedData = exceldata.data.configuration.find((config) => config._id === item._id);
      if (!matchedData?.catalogue) return;

      setChecked(matchedData?.status === "active");

      const transformedData = transformToColumnWise(matchedData.catalogue);
      const formattedFloors = matchedData.catalogue.map((i) => formatFloor(i.floor));

      setFloors(formattedFloors);
      setTowerData(transformedData);
      setUnits([]); // reset selection on tower change
    },
    [exceldata, transformToColumnWise]
  );

  // Max grid column & row counts
  const getMaxPosition = (arr) => (Array.isArray(arr) ? Math.max(...arr.map((item) => item.position)) : 0);
  const maxPosition = getMaxPosition(towerData);
  const getMaxFloorNumber = (arr) => (Array.isArray(arr) ? Math.max(...arr.map((item) => (item === "G" ? 0 : item))) : 0);
  const maxFloor = getMaxFloorNumber(floors);

  useEffect(() => {
    if (enabled) {
      toast.success("Tower Status Changed Successfully!");
      return;
    } else if (enableError) {
      toast.error("Error changing status of this tower");
      return;
    }
  }, [enabled, enableError]);

  // Fetch excel data on params change
  useEffect(() => {
    dispatch(viewExcel(project_id, category_id, project_type));
    return () => {
      dispatch({ type: "RESET_EXCEL" });
    };
  }, [project_id, category_id, dispatch, project_type]);

  // On exceldata load set first tower default
  useEffect(() => {
    if (exceldata) {
      renderCatalogue(exceldata?.data?.towers?.[0]);
    }
  }, [exceldata, renderCatalogue]);

  // Body scroll lock for modal open
  useEffect(() => {
    if (unitData) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => document.body.classList.remove("modal-open");
  }, [unitData]);

  // Handle unit click to toggle selection
  const handleUnitClick = (unit) => {
    const isAllotted = unit.tempData.is_allotted === true;
    if (isAllotted) return; // Do nothing on allotted units

    const unitStatus = unit.tempData.status || unit.status || "";
    if (unitStatus !== "avl") {
      toast.error("This unit cannot be selected because it is unavailable");
      return;
    }

    const newUnitObj = {
      catalogue: towerID,
      unit: unit.tempData.type || "",
      catalogue_unit_id: unit.tempData.catalogue_unit_id,
      unitno: unit.id,
      index: unit.tempData.index || "",
      floor: unit.startFloor || 0,
      lane: unit.lane || 0,
    };

    const isSelected = units.some((u) => u.catalogue_unit_id === newUnitObj.catalogue_unit_id);

    if (!isSelected) {
      setUnits((prev) => [...prev, newUnitObj]);
    } else {
      setUnits((prev) => prev.filter((u) => u.catalogue_unit_id !== newUnitObj.catalogue_unit_id));
    }
  };

  // Select all available units in tower
  const handleSelectAllCatalogue = () => {
    if (!towerData || !units) return;

    // Select only units that are available and NOT allotted
    const availableUnits = towerData.filter((unit) => unit.status === "avl" && unit.tempData.is_allotted !== true);
    const newSelectedUnits = availableUnits.map((unit) => ({
      catalogue: towerID,
      unit: unit.tempData.type || "",
      catalogue_unit_id: unit.tempData.catalogue_unit_id,
      unitno: unit.id,
      index: unit.tempData.index || "",
      floor: unit.startFloor || 0,
      lane: unit.lane || 0,
    }));

    setUnits(newSelectedUnits);
  };

  // Assign selected units
  const handleAssignCatalogue = () => {
    if (units.length === 0) {
      toast.error("Please select at least one unit before assigning.");
      return;
    }

    const payload = {
      franchise_id: franchiseId,
      builder_id: builderId,
      project_id: project_id,
      catalogue_id: towerID,
      units: units.map((u) => ({
        unit: u.unit,
        catalogue_unit_id: u.catalogue_unit_id,
        index: u.index,
        unitno: Number(u.unitno) || 0,
        floor: u.floor,
        lane: u.lane,
      })),
    };

    dispatch(assignCatalogue(payload))
      .then(() => {
        toast.success("Catalogue assigned successfully!");
        setUnits([]); 
        dispatch(viewExcel(project_id, category_id, project_type));
      })
      .catch((err) => {
        toast.error("Error assigning catalogue: " + (err?.message || "Failed"));
      });
  };

  return (
    <div className="catalogue-view-page-wrapper">
      <div className="catalogue-view-page-header flex-container flex-align-center flex-justify-between">
        <div className="flex-container flex-align-center">
          <BreadCrumbs
            title="Project Details"
            routeName1="Project List"
            routeName2="Project View"
            route1="/projects"
          />
          <div className="catalogue-view-page-header-right gap-10 flex-container flex-align-center">
            {exceldata?.data?.towers?.map((item, index) => (
              <button
                key={item._id}
                className={`flex-container flex-align-center ${towerID === item._id ? "active-catalogue" : ""}`}
                id={`tower-btn-${index}`}
                onClick={() => renderCatalogue(item)}
              >
                <TowerIcon />
                <span>{item.tower_name}</span>
              </button>
            ))}
          </div>
        </div>
        <div style={{ marginBottom: "1rem", textAlign: "right" }}>
          <button className="assign-catalogue-btn" onClick={handleAssignCatalogue}>
            Assign Catalogue
          </button>
          <button className="select-all-catalogue-btn" onClick={handleSelectAllCatalogue}>
            Select All Catalogue
          </button>
        </div>
      </div>

      {towerData && (
        <div className="catalogue-view-container flex-container">
          <div className="catalogue-view-floors commercial-floor-tile border-8 flex-container flex-align-center">
            {floors.map((item) => (
              <div key={item} className="catalogue-floor-tile commercial-catalogue-floor-tile mirror border-8">
                <span>{item}</span>
              </div>
            ))}
          </div>
          <div
            className="mirror catalogue-map-container catalogue-commercial-map-container grid-container"
            style={{
              gridTemplateColumns: `repeat(${maxPosition}, 1fr)`,
              gridTemplateRows: `repeat(${maxFloor}, 74px)`,
              transform: "scaleY(-1)",
            }}
          >
            {towerData.map((unit) => {
              const isSelected = units.some((u) => u.catalogue_unit_id === unit.tempData.catalogue_unit_id);
              const isAllotted = unit.tempData.is_allotted === true;

              return (
                <div
                  key={unit.id}
                  className="mirror catalogue-unit-tile-main commercial-catalogue-unit-tile-main gap-15 border-8 flex-container flex-align-center"
                  style={{
                    gridColumn: unit.position,
                    gridRow: `${unit.startFloor === "G" ? 1 : unit.startFloor + 1} / ${
                      unit.endFloor === "G" ? 2 : unit.endFloor + 2
                    }`,
                    transform: "scaleY(-1)",
                    border: isAllotted
                      ? "2px solid green"
                      : isSelected
                      ? "2px solid green"
                      : unit.tempData.status === "unavl"
                      ? "2px solid rgb(230, 73, 0)"
                      : unit.status === "avl"
                      ? "2px solid rgb(6, 170, 0)"
                      : unit.status === "onhold"
                      ? "2px solid rgb(198, 205, 0)"
                      : unit.status === "sold"
                      ? "2px solid rgb(189, 0, 0)"
                      : "none",
                    cursor: isAllotted ? "not-allowed" : unit.tempData.status === "avl" ? "pointer" : "default",
                    backgroundColor: isAllotted ? "lightgreen" : "inherit",
                  }}
                  onClick={() => {
                    if (isAllotted) return; // Prevent clicking allotted units
                    handleUnitClick(unit);
                  }}
                >
                  <div
                    className="catalogue-tile-unit-no mirror flex-container flex-center"
                    style={{
                      backgroundColor: isAllotted ? "green" : isSelected ? "green" : "#686868",
                      border: "none",
                    }}
                  >
                    <span title={unit.tempData.unitno}>{unit.tempData.unitno}</span>
                  </div>
                  <div className="catalogue-tile-unit-type" style={{ textTransform: "capitalize" }}>
                    <span>{`${unit.tempData.type} - ${unit.tempData.subtype}`}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {unitData && <UnitDetailsModal unit={unitData} onClose={() => setUnitData(null)} />}

      {/* Uncomment if needed */}
      {/* {update && <UpdateExcel id={towerID} onClose={() => setUpdate(false)} />} */}
    </div>
  );
};

export default ViewCommercialCatalogue;
